import * as React from "react";

import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";

import { useTutorialProgress } from "../../hooks/useTutorialProgress";
import EditorWelcomeModal from "./EditorWelcomeModal";

type EditorTutorialStep = "welcome" | "navigation";

const STORAGE_KEY = "replo:tutorial:editor:uiStep";

function isEditorTutorialStep(value: unknown): value is EditorTutorialStep {
  return value === "welcome" || value === "navigation";
}

interface EditorTutorialContextValue {
  isActive: boolean;
  currentStep: EditorTutorialStep;
  onDismiss: () => void;
  goToNavigationGuide: () => void;
}

const EditorTutorialContext =
  React.createContext<EditorTutorialContextValue | null>(null);

export function useEditorTutorial() {
  return React.useContext(EditorTutorialContext);
}

type EditorTutorialProviderProps = React.PropsWithChildren<{
  isEditorReady?: boolean;
}>;

export function EditorTutorialProvider({
  children,
  isEditorReady = true,
}: EditorTutorialProviderProps) {
  const isEnabled = isFeatureEnabled("user-activation-tutorial");
  const [currentStep, setCurrentStep] =
    useLocalStorageState<EditorTutorialStep>(STORAGE_KEY, "welcome", {
      validate: isEditorTutorialStep,
    });

  const {
    shouldShowTutorial,
    skipTutorial,
    isLoading: isLoadingTutorialProgress,
    isUpdating: isUpdatingTutorialProgress,
  } = useTutorialProgress({
    enabled: isEnabled,
    tutorialLocation: "editor",
  });

  // Show tutorial when feature is enabled, user should see it, and editor UI is ready.
  // Also hide during mutation to prevent flash of first step when dismissing.
  const isActive =
    isEnabled &&
    shouldShowTutorial &&
    !isLoadingTutorialProgress &&
    !isUpdatingTutorialProgress &&
    isEditorReady;

  const handleDismiss = React.useCallback(() => {
    skipTutorial();
    setCurrentStep(null);
  }, [skipTutorial, setCurrentStep]);

  const goToNavigationGuide = React.useCallback(() => {
    setCurrentStep("navigation");
  }, [setCurrentStep]);

  const contextValue = React.useMemo<EditorTutorialContextValue>(
    () => ({
      isActive,
      currentStep,
      onDismiss: handleDismiss,
      goToNavigationGuide,
    }),
    [isActive, currentStep, handleDismiss, goToNavigationGuide],
  );

  return (
    <EditorTutorialContext.Provider value={contextValue}>
      {children}
      <EditorWelcomeModal
        isOpen={isActive && currentStep === "welcome"}
        onNext={goToNavigationGuide}
      />
    </EditorTutorialContext.Provider>
  );
}
